import React, { useState } from 'react';

interface ImageWithLoaderProps {
  src: string;
  alt: string;
  style?: React.CSSProperties;
  /**
   * TRIL-620: report load state so a parent can gate an affordance on it.
   *
   * The error state lives in here, but any sibling control — an enlarge trigger, say — is
   * unaffected by it and would happily act on an image that never loaded.
   */
  onStatusChange?: (status: 'loading' | 'loaded' | 'error') => void;
}

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
  src,
  alt,
  style,
  onStatusChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const handleLoad = () => {
    setLoading(false);
    onStatusChange?.('loaded');
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
    // Reported even though a never-loaded image is already excluded by gating on 'loaded': an
    // <img> can error AFTER a successful load (a revoked blob, a decode failure), and without
    // this the affordance would survive on an image the reader can no longer see.
    onStatusChange?.('error');
  };

  // The PREVIOUS src, not a "have I mounted" flag.
  //
  // A flag counts effect INVOCATIONS, and StrictMode re-invokes mount effects on the same fiber
  // in development — the ref is already true the second time, so the reset runs anyway and the
  // guard is dev-only theatre. Comparing the src is immune: it asks the question that actually
  // matters ("did the image change?") rather than inferring it from how many times this has run.
  const prevSrc = React.useRef(src);

  // A NEW src is a new attempt: without this, an image that failed keeps its parent's affordance
  // hidden after being pointed at a perfectly good URL — the same defect TRIL-634 fixed on mobile
  // by keying its effect on the uri.
  React.useEffect(() => {
    // Only a genuinely NEW src resets. The initial state is already 'loading', so resetting for
    // the same src is at best a no-op — and at worst a real regression: a CACHED image can fire
    // onLoad before this effect runs, and the reset would discard that load, hiding the
    // affordance on an image the reader can plainly see.
    if (prevSrc.current === src) return;
    prevSrc.current = src;
    setLoading(true);
    setError(false);
    onStatusChange?.('loading');
    // onStatusChange is excluded deliberately: consumers pass an inline arrow, so including it
    // would re-run this on every parent render and reset a loaded image back to loading.
    // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  }, [src]);

  return (
    <div>
      {loading && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            position: 'relative',
          }}
        >
          <div
            className="skeleton"
            aria-hidden="true"
            style={{ width: '100%', height: '200px' }}
          />
        </div>
      )}

      {error ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
          }}
        >
          <p>Failed to load image</p>
        </div>
      ) : (
        <img
          alt={alt}
          src={src}
          style={{
            ...style,
            display: loading ? 'none' : 'block',
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};

export { ImageWithLoader };
